import { useState, useRef } from "react";
import { Search, Folder, Image, Film, X, ChevronRight, Home, MoreVertical, Check } from "lucide-react";
import { useMedia, type MediaFile, type MediaFolder } from "../../screens/media/MediaProvider";

export type PickMode = "file" | "folder";

export type PickSelection = { kind: "file"; items: MediaFile[] } | { kind: "folder"; items: MediaFolder[] };

type Props = {
  // Mode
  pickMode?: PickMode;
  allowMultiple?: boolean;

  // Picker callbacks
  onPickSingle?: (item: MediaFile | MediaFolder) => void;
  onPickMultiple?: (selection: PickSelection) => void;

  // Visual highlight for items already chosen elsewhere (non-interactive)
  highlightIds?: string[];

  // Layout
  showFiles?: boolean;
  showSearch?: boolean;
  title?: string;
  onClose?: () => void;

  // Library-mode slots
  renderFolderMenu?: (folder: MediaFolder, close: () => void) => React.ReactNode;
  onFileSelect?: (file: MediaFile | null) => void;
  onFolderSelect?: (folder: MediaFolder | null) => void;
  // Called when the user clicks empty space inside the browser (not a card / button).
  onBackgroundClick?: () => void;

  // Extra content rendered below the grids (e.g. UploadZone in library mode)
  children?: React.ReactNode;
};

const formatSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SearchBar = () => {
  const { searchQuery, setSearchQuery } = useMedia();
  const [value, setValue] = useState(searchQuery);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleChange = (next: string) => {
    setValue(next);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setSearchQuery(next), 300);
  };

  return (
    <div className="relative">
      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        placeholder="Search files..."
        className="w-64 pl-9 pr-8 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-blue-500"
      />
      {value && (
        <button
          onClick={() => handleChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
        >
          <X size={14} />
        </button>
      )}
    </div>
  );
};

const Breadcrumbs = () => {
  const { breadcrumbs, navigateToFolder, isSearching } = useMedia();

  if (isSearching) {
    return (
      <div className="flex items-center gap-2 text-sm text-zinc-400">
        <Search size={14} />
        <span>Search Results</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 text-sm flex-wrap">
      <button
        onClick={() => navigateToFolder(null)}
        className="flex items-center gap-1 text-zinc-400 hover:text-white transition-colors"
      >
        <Home size={14} />
        <span>Root</span>
      </button>
      {breadcrumbs.map((folder) => (
        <div key={folder.id} className="flex items-center gap-1">
          <ChevronRight size={14} className="text-zinc-600" />
          <button
            onClick={() => navigateToFolder(folder.id)}
            className="text-zinc-400 hover:text-white transition-colors"
          >
            {folder.name}
          </button>
        </div>
      ))}
    </div>
  );
};

type FolderCardProps = {
  folder: MediaFolder;
  pickMode?: PickMode;
  isPicked: boolean;
  isHighlighted: boolean;
  onNavigate: () => void;
  onPick?: () => void;
  renderMenu?: (folder: MediaFolder, close: () => void) => React.ReactNode;
};

const FolderCard = ({ folder, pickMode, isPicked, isHighlighted, onNavigate, onPick, renderMenu }: FolderCardProps) => {
  const [showMenu, setShowMenu] = useState(false);
  const pathOnly = folder.isPathOnly;
  const isFolderPicker = pickMode === "folder";

  // Both folder-picker mode AND library mode use: single-click selects, double-click navigates.
  // (File-picker mode also uses select behavior, since the folder isn't the picker target.)
  const handleCardClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onPick) {
      onPick();
      return;
    }
    onNavigate();
  };

  const handleCardDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onNavigate();
  };

  return (
    <div
      onClick={handleCardClick}
      onDoubleClick={handleCardDoubleClick}
      title={
        onPick
          ? "Click to select · Double-click to open"
          : pathOnly
            ? "You can browse into this folder but cannot edit it"
            : undefined
      }
      className={`group relative border rounded-lg p-4 cursor-pointer select-none transition-all ${
        isPicked
          ? "bg-blue-500/10 border-blue-500 ring-2 ring-blue-500/20"
          : pathOnly
            ? "bg-zinc-900/40 border-zinc-800 hover:border-zinc-700"
            : "bg-zinc-800/50 border-zinc-700/50 hover:border-zinc-600"
      } ${isHighlighted ? "opacity-60" : ""}`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${pathOnly ? "bg-zinc-700/30" : "bg-blue-500/20"}`}>
          <Folder size={24} className={pathOnly ? "text-zinc-500" : "text-blue-400"} />
        </div>
        <div className="flex-1 min-w-0">
          <p className={`font-medium truncate ${pathOnly ? "text-zinc-400" : "text-white"}`}>{folder.name}</p>
          <p className="text-xs text-zinc-500 mt-1">
            {pathOnly ? "Path only" : `${folder.fileCount ?? 0} files, ${folder.subfolderCount ?? 0} folders`}
          </p>
        </div>

        {/* Library-mode menu (rename/delete) */}
        {!pickMode && folder.canEdit && renderMenu && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowMenu(!showMenu);
            }}
            className="opacity-0 group-hover:opacity-100 p-1 hover:bg-zinc-700 rounded transition-all"
          >
            <MoreVertical size={16} className="text-zinc-400" />
          </button>
        )}

        {/* Picked indicator */}
        {isPicked && (
          <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
            <Check size={12} className="text-white" />
          </div>
        )}
      </div>

      {showMenu && !pickMode && folder.canEdit && renderMenu && (
        <div
          className="absolute right-2 top-12 bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-10 py-1 min-w-32"
          onClick={(e) => e.stopPropagation()}
        >
          {renderMenu(folder, () => setShowMenu(false))}
        </div>
      )}
    </div>
  );
};

type FileCardProps = {
  file: MediaFile;
  selected: boolean;
  isHighlighted: boolean;
  onClick: () => void;
};

const FileCard = ({ file, selected, isHighlighted, onClick }: FileCardProps) => {
  const isImage = file.contentType.startsWith("image/");
  const isVideo = file.contentType.startsWith("video/");

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      className={`group relative bg-zinc-800/50 border rounded-lg overflow-hidden cursor-pointer transition-all ${
        selected ? "border-blue-500 ring-2 ring-blue-500/20" : "border-zinc-700/50 hover:border-zinc-600"
      } ${isHighlighted ? "opacity-60" : ""}`}
    >
      {selected && (
        <div className="absolute top-2 right-2 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center z-10">
          <Check size={12} className="text-white" />
        </div>
      )}

      <div className="aspect-video bg-zinc-900 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img
            src={file.downloadUrl}
            alt={file.originalFilename}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : isVideo ? (
          <video src={file.downloadUrl} className="w-full h-full object-cover" muted preload="metadata" />
        ) : (
          <Film size={32} className="text-zinc-600" />
        )}
      </div>

      <div className="p-3">
        <p className="text-sm font-medium text-white truncate" title={file.originalFilename}>
          {file.originalFilename}
        </p>
        <div className="flex items-center gap-2 mt-1">
          {isImage ? <Image size={12} className="text-zinc-500" /> : <Film size={12} className="text-zinc-500" />}
          <span className="text-xs text-zinc-500">{formatSize(file.sizeBytes)}</span>
        </div>

        {file.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {file.tags.slice(0, 2).map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 bg-zinc-700 rounded text-xs text-zinc-300">
                {tag}
              </span>
            ))}
            {file.tags.length > 2 && (
              <span className="px-1.5 py-0.5 text-xs text-zinc-500">+{file.tags.length - 2}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const MediaBrowser = ({
  pickMode,
  allowMultiple = false,
  onPickSingle,
  onPickMultiple,
  highlightIds = [],
  showFiles: showFilesProp,
  showSearch: showSearchProp,
  title,
  onClose,
  renderFolderMenu,
  onFileSelect,
  onFolderSelect,
  onBackgroundClick,
  children,
}: Props) => {
  const { files, folders, loading, error, navigateToFolder, isSearching, currentFolderId } = useMedia();

  const showFiles = showFilesProp ?? pickMode !== "folder";
  const isPicker = !!pickMode;
  // Search hits files only — hide it when files aren't shown.
  const showSearch = showSearchProp ?? showFiles;

  const [pickedFileIds, setPickedFileIds] = useState<Set<string>>(new Set());
  const [pickedFolderIds, setPickedFolderIds] = useState<Set<string>>(new Set());

  const highlightSet = new Set(highlightIds);

  const handlePickFile = (file: MediaFile) => {
    if (!allowMultiple) {
      onPickSingle?.(file);
      return;
    }
    setPickedFileIds((prev) => {
      const next = new Set(prev);
      if (next.has(file.id)) next.delete(file.id);
      else next.add(file.id);
      return next;
    });
  };

  const handlePickFolder = (folder: MediaFolder) => {
    if (!allowMultiple) {
      // Single-select: replace selection. User confirms via Done button;
      // double-clicking navigates into the folder instead.
      setPickedFolderIds(new Set([folder.id]));
      return;
    }
    setPickedFolderIds((prev) => {
      const next = new Set(prev);
      if (next.has(folder.id)) next.delete(folder.id);
      else next.add(folder.id);
      return next;
    });
  };

  const handleFileClick = (file: MediaFile) => {
    if (isPicker && pickMode === "file") {
      handlePickFile(file);
      return;
    }
    if (!isPicker) {
      onFileSelect?.(file);
    }
  };

  const handleConfirm = () => {
    if (pickMode === "folder") {
      const items = folders.filter((f) => pickedFolderIds.has(f.id));
      if (allowMultiple) {
        onPickMultiple?.({ kind: "folder", items });
      } else if (items[0]) {
        onPickSingle?.(items[0]);
      }
      return;
    }
    if (pickMode === "file") {
      const items = files.filter((f) => pickedFileIds.has(f.id));
      if (allowMultiple) {
        onPickMultiple?.({ kind: "file", items });
      } else if (items[0]) {
        onPickSingle?.(items[0]);
      }
    }
  };

  const pickedCount = pickMode === "file" ? pickedFileIds.size : pickedFolderIds.size;
  // Show the confirm bar for any multi-select picker, and for single-select folder picker
  // (which requires confirmation since single-click selects rather than auto-picks).
  const showConfirmBar = isPicker && (allowMultiple || pickMode === "folder");

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="p-4 border-b border-zinc-800 space-y-3">
        {(title || onClose) && (
          <div className="flex items-center justify-between">
            {title ? <h2 className="text-lg font-semibold text-white">{title}</h2> : <div />}
            {onClose && (
              <button onClick={onClose} className="p-1 hover:bg-zinc-800 rounded">
                <X size={20} className="text-zinc-400" />
              </button>
            )}
          </div>
        )}
        <div className="flex items-center justify-between gap-3">
          <Breadcrumbs />
          {showSearch && <SearchBar />}
        </div>
      </div>

      <div
        className="flex-1 overflow-y-auto p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onBackgroundClick?.();
        }}
      >
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">{error}</div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-zinc-500">Loading...</div>
          </div>
        ) : (
          <>
            {!isSearching && folders.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-zinc-400 mb-3 uppercase tracking-wide">Folders</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {folders.map((folder) => {
                    const onPick =
                      pickMode === "folder"
                        ? () => handlePickFolder(folder)
                        : !isPicker && onFolderSelect
                          ? () => onFolderSelect(folder)
                          : undefined;
                    return (
                      <FolderCard
                        key={folder.id}
                        folder={folder}
                        pickMode={pickMode}
                        isPicked={pickedFolderIds.has(folder.id)}
                        isHighlighted={highlightSet.has(folder.id)}
                        onNavigate={() => navigateToFolder(folder.id)}
                        onPick={onPick}
                        renderMenu={renderFolderMenu}
                      />
                    );
                  })}
                </div>
              </div>
            )}

            {showFiles && (isSearching || currentFolderId || files.length > 0) && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-zinc-400 mb-3 uppercase tracking-wide">
                  {isSearching ? "Search Results" : "Files"}
                  {files.length > 0 && <span className="ml-2 text-zinc-500">({files.length})</span>}
                </h3>
                {files.length === 0 ? (
                  <div className="text-center py-12">
                    <Image size={48} className="mx-auto text-zinc-700 mb-3" />
                    <p className="text-zinc-500">
                      {isSearching ? "No files match your search" : "No files in this folder"}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {files.map((file) => (
                      <FileCard
                        key={file.id}
                        file={file}
                        selected={pickMode === "file" && allowMultiple ? pickedFileIds.has(file.id) : false}
                        isHighlighted={highlightSet.has(file.id)}
                        onClick={() => handleFileClick(file)}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {!isSearching && !currentFolderId && folders.length === 0 && (
              <div className="text-center py-12">
                <Folder size={48} className="mx-auto text-zinc-700 mb-3" />
                <p className="text-zinc-500">No folders yet — create one to get started.</p>
              </div>
            )}

            {!isPicker && !isSearching && children}
          </>
        )}
      </div>

      {showConfirmBar && (
        <div className="border-t border-zinc-800 p-4 flex items-center justify-between">
          <p className="text-sm text-zinc-500">
            {pickedCount === 0 ? "Nothing selected" : `${pickedCount} ${pickedCount === 1 ? "item" : "items"} selected`}
          </p>
          <div className="flex gap-3">
            {onClose && (
              <button onClick={onClose} className="px-4 py-2 text-zinc-400 hover:text-white">
                Cancel
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={pickedCount === 0}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white rounded-lg"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

type ModalProps = Props & {
  isOpen: boolean;
};

/** Convenience: render MediaBrowser inside a modal backdrop. Callers must still wrap in MediaProvider. */
export const MediaBrowserModal = ({ isOpen, ...browserProps }: ModalProps) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50" onClick={browserProps.onClose}>
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-4xl h-[80vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <MediaBrowser {...browserProps} />
      </div>
    </div>
  );
};
